import type { CellValue } from 'exceljs';
import { BudgetImportRow } from 'types/budgetImport';

// cabeçalho da planilha (sem acento, espaço ou pontuação) -> campo enviado à API
const COLUMNS: [string, string][] = [
  ['ndoorcamento', 'number'],
  ['datadecriacao', 'created_at'],
  ['datadaultimaatualizacao', 'updated_at'],
  ['criadopor', 'created_by'],
  ['status', 'status'],
  ['cpfdopaciente', 'patient_cpf'],
  ['nomedopaciente', 'patient_name'],
  ['procedimento', 'procedure'],
  ['especialidade', 'specialty'],
  ['mediconome', 'doctor_name'],
  ['crmoucpfdomedico', 'doctor_key'],
  ['hospitalrazaosocial', 'hospital_name'],
  ['cnpjdohospital', 'hospital_cnpj'],
  ['valordomedico', 'value_doctor'],
  ['valordohospital', 'value_hospital'],
  ['valordoanestesista', 'value_anesthetist'],
  ['valordospacotes', 'value_packages'],
  ['custodoorcamento', 'value_cost'],
  ['valordevenda', 'value_sale'],
  ['lucro', 'profit'],
  ['margemdelucro', 'margin'],
  ['fechadopor', 'closed_by'],
  ['datadofechamento', 'closed_at'],
  ['valordeentrada', 'value_entry'],
  ['valorpago', 'value_paid'],
  ['canceladopor', 'cancelled_by'],
  ['datadocancelamento', 'cancelled_at'],
];

const EXAMPLE = 'EXEMPLO';

export interface ParsedBudgetWorkbook {
  rows: BudgetImportRow[];
  // linhas preenchidas na aba de pacotes, que ainda não são lidas
  packageRows: number;
}

function normalize(text: string) {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function pad(value: number) {
  return String(value).padStart(2, '0');
}

// a data da célula vem à meia-noite UTC; usar o fuso local deslocaria o dia
function cellValue(value: CellValue): string | number | null {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) {
    return `${value.getUTCFullYear()}-${pad(value.getUTCMonth() + 1)}-${pad(value.getUTCDate())}`;
  }
  if (typeof value === 'object') {
    if ('result' in value) return cellValue(value.result as CellValue);
    if ('richText' in value) return value.richText.map(part => part.text).join('');
    if ('text' in value) return String(value.text);
    return null;
  }
  if (typeof value === 'string') return value.trim() || null;
  if (typeof value === 'boolean') return null;
  return value;
}

export async function parseBudgetWorkbook(file: File): Promise<ParsedBudgetWorkbook> {
  const { Workbook } = await import('exceljs');
  const workbook = new Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());

  const sheet = workbook.worksheets.find(item => normalize(item.name) === 'orcamentos');
  if (!sheet) throw new Error('A planilha não tem a aba "Orçamentos".');

  const fields = new Map<number, string>();
  sheet.getRow(1).eachCell((cell, column) => {
    const header = normalize(String(cellValue(cell.value) ?? ''));
    const match = COLUMNS.find(([prefix]) => header.startsWith(prefix));
    if (match) fields.set(column, match[1]);
  });

  if (!fields.size) throw new Error('Não encontrei os cabeçalhos do modelo na aba "Orçamentos".');

  const rows: BudgetImportRow[] = [];

  sheet.eachRow((sheetRow, rowIndex) => {
    if (rowIndex === 1) return;

    const row: BudgetImportRow = {};
    fields.forEach((field, column) => {
      row[field] = cellValue(sheetRow.getCell(column).value);
    });

    if (row.number === null || String(row.number).toUpperCase() === EXAMPLE) return;
    rows.push(row);
  });

  const packagesSheet = workbook.worksheets.find(item => normalize(item.name) === 'pacotesdoorcamento');
  let packageRows = 0;
  packagesSheet?.eachRow((sheetRow, rowIndex) => {
    if (rowIndex === 1) return;
    const first = cellValue(sheetRow.getCell(1).value);
    if (first !== null && String(first).toUpperCase() !== EXAMPLE) packageRows += 1;
  });

  return { rows, packageRows };
}
